import type { Labels } from "./types";

/** One tenant label override as stored in `tenant.localization.labelOverrides[].overrides[]`. */
export interface LabelOverride {
    /** Dot path into the label dictionary, e.g. "standings.title". */
    key: string;
    value: string;
}

/**
 * Applies tenant overrides on top of a locale's default dictionary.
 *
 * Pure and shared by the server (`getLabels`) and the per-locale client providers, so both
 * sides compute the same dictionary from the same inputs and hydration never disagrees.
 * With no overrides the defaults are returned as-is (read-only, shared); otherwise a deep
 * clone is patched so the bundled defaults are never mutated.
 */
export function applyLabelOverrides(defaults: Labels, overrides: readonly LabelOverride[]): Labels {
    if (overrides.length === 0) return defaults;
    return patchLabelOverrides(structuredClone(defaults), overrides);
}

/**
 * Applies overrides onto `target` in place and returns it. For callers that already own a
 * private copy (the server memoizes one dictionary per tenant and locale).
 */
export function patchLabelOverrides(target: Labels, overrides: readonly LabelOverride[]): Labels {
    const obj = target as unknown as Record<string, unknown>;
    for (const override of overrides) {
        if (!override || typeof override.key !== "string" || typeof override.value !== "string") continue;
        setNestedValue(obj, override.key, override.value);
    }
    return target;
}

/** Sets `value` at the dot `path`, creating intermediate objects as needed. */
export function setNestedValue(obj: Record<string, unknown>, path: string, value: string): void {
    const keys = path.split(".");
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const next = current[keys[i]];
        if (typeof next !== "object" || next === null) current[keys[i]] = {};
        current = current[keys[i]] as Record<string, unknown>;
    }
    current[keys[keys.length - 1]] = value;
}
